import { useAuthToken } from '@/hooks/useAuthToken'
import { bugReportService } from '@/services/bugReportService'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

/** Mirrors `support_attachment_max_files` on the backend. */
export const MAX_ATTACHMENTS = 5
/** Mirrors `support_attachment_max_bytes` on the backend. */
export const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024
/** The same ceiling as the copy states it, so the number is written down once. */
export const MAX_ATTACHMENT_MB = MAX_ATTACHMENT_BYTES / (1024 * 1024)

/**
 * Extensions the backend will accept. It re-checks by sniffing the file's own
 * leading bytes, so this list only exists to fail fast and to populate the file
 * picker's filter — it is not the security boundary.
 */
export const ACCEPTED_ATTACHMENT_TYPES = '.png,.jpg,.jpeg,.gif,.webp,.pdf,.txt,.log,.json,.har,.csv'

const ACCEPTED_EXTENSIONS = new Set(ACCEPTED_ATTACHMENT_TYPES.split(',').map((ext) => ext.slice(1)))

export type AttachmentItem = {
  /** Client-side key. Distinct from `remoteId`, which only exists once uploaded. */
  localId: string
  name: string
  size: number
  status: 'uploading' | 'done' | 'error'
  /** The handle the bug report cites. Present only when `status` is `done`. */
  remoteId?: string
  /** Object URL for an image thumbnail; revoked when the item goes away. */
  previewUrl?: string
  error?: string
}

function extensionOf(name: string): string {
  const index = name.lastIndexOf('.')
  return index === -1 ? '' : name.slice(index + 1).toLowerCase()
}

/**
 * Owns the attachment list for the bug form.
 *
 * Files upload as soon as they're picked rather than at submit time, so a file
 * that's too big or of the wrong type fails against that file while the reporter
 * is still writing, and submitting stays fast. The trade is orphaned objects in
 * the bucket when someone attaches a file and then cancels — cheap, and the
 * alternative (uploading on submit) makes every failure a failure of the whole
 * report.
 */
export function useBugAttachments() {
  const { t } = useTranslation()
  const { getToken } = useAuthToken()
  const [items, setItems] = useState<AttachmentItem[]>([])

  // Reset/unmount needs the current previews, but must not re-run on every change.
  const itemsRef = useRef(items)
  itemsRef.current = items

  const revokePreview = useCallback((item: AttachmentItem) => {
    if (item.previewUrl) URL.revokeObjectURL(item.previewUrl)
  }, [])

  const upload = useCallback(
    async (localId: string, file: File) => {
      try {
        const uploaded = await bugReportService.uploadAttachment(file, getToken)
        setItems((current) =>
          current.map((item) => (item.localId === localId ? { ...item, status: 'done', remoteId: uploaded.id } : item))
        )
      } catch (error) {
        console.error('Failed to upload bug attachment:', error)
        setItems((current) =>
          current.map((item) =>
            item.localId === localId
              ? { ...item, status: 'error', error: t('reportBug.attachments.uploadFailed') }
              : item
          )
        )
      }
    },
    [getToken, t]
  )

  const addFiles = useCallback(
    (files: File[]) => {
      if (files.length === 0) return

      // Built from the ref rather than inside a state updater so each item stays
      // paired with its own File — two screenshots can share a name and a size.
      const current = itemsRef.current
      let remaining = MAX_ATTACHMENTS - current.length
      const created: { item: AttachmentItem; file: File }[] = []

      for (const file of files) {
        if (remaining <= 0) break
        remaining -= 1

        const item: AttachmentItem = {
          localId: crypto.randomUUID(),
          name: file.name,
          size: file.size,
          status: 'uploading',
          previewUrl: file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined,
        }

        // Pre-checks that mirror the backend's, so an obviously bad file reports
        // against itself immediately instead of after a round trip.
        if (file.size > MAX_ATTACHMENT_BYTES) {
          item.status = 'error'
          item.error = t('reportBug.attachments.tooLarge', { size: MAX_ATTACHMENT_MB })
        } else if (!ACCEPTED_EXTENSIONS.has(extensionOf(file.name))) {
          item.status = 'error'
          item.error = t('reportBug.attachments.wrongType')
        }

        created.push({ item, file })
      }

      if (created.length === 0) return

      const next = [...current, ...created.map((entry) => entry.item)]
      itemsRef.current = next // keep a second addFiles in the same tick honest
      setItems(next)

      for (const { item, file } of created) {
        if (item.status === 'uploading') void upload(item.localId, file)
      }
    },
    [t, upload]
  )

  const remove = useCallback(
    (localId: string) => {
      setItems((current) => {
        const target = current.find((item) => item.localId === localId)
        if (target) revokePreview(target)
        return current.filter((item) => item.localId !== localId)
      })
    },
    [revokePreview]
  )

  const reset = useCallback(() => {
    for (const item of itemsRef.current) revokePreview(item)
    setItems([])
  }, [revokePreview])

  // Release any previews still held when the form unmounts.
  useEffect(
    () => () => {
      for (const item of itemsRef.current) revokePreview(item)
    },
    [revokePreview]
  )

  return {
    items,
    addFiles,
    remove,
    reset,
    isUploading: items.some((item) => item.status === 'uploading'),
    isFull: items.length >= MAX_ATTACHMENTS,
    attachmentIds: items.flatMap((item) => (item.remoteId ? [item.remoteId] : [])),
  }
}

export type BugAttachments = ReturnType<typeof useBugAttachments>
